import React, { useCallback, useEffect, useState } from "react";
import { Logo } from "../../components/Icons";
import { FormInputField, Label } from "../../components";
import CommonButton from "../../components/Buttons/CommonButton";
import SmallButton from "../../components/Buttons/SmallButton";
import Footer from "../../components/Footer";
import { useDispatch, useSelector } from "react-redux";
import { useForm, useModules } from "../../hooks";
import { sendGuestOtp, verifyEmail } from "../../redux/actions/register/otp";
import RegistrationOTPModal from "../../components/Modals/RegistrationOTPModal";
import CountriesInput from "../../components/Common/CountriesInput";
import OptionButton from "../../components/Common/OptionButton";
import useAllCountries from "../../hooks/useAllCountries";
import { setPageTitle } from "../../helper/metaDataHelper";
import PasswordToggleIcon from "../../components/Common/PasswordToggleIcon";
import ErrorMessage from "../../components/Common/ErrorMessage";
import Skeleton from "../../components/Common/Skeleton";
import Slider from "../../components/Login/Slider";
import { getModuleStatus } from "../../redux/actions/lookups/getModuleStatus";
import { urlExists } from "../../helper/urlExists";

const Signup = () => {
  const allCountries = useAllCountries();
  const [country, setCountry] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const company = useSelector((state) => state.ownCompany.company);
  const isSumSubEnabled = useModules("Sumsub");

  const name = useForm("");
  const introducer = useForm("");
  const companyType = useForm("");
  const email = useForm("", "email");
  const mobileNumber = useForm("");
  const password = useForm("", "password");
  const confirmPassword = useForm("", "password");
  const acceptTermsAndConditions = useForm(false);
  const [showSlider, setShowSlider] = useState(false);

  const [showPassword, setShowPassword] = useState({
    new_password: false,
    new_password_confirmation: false,
  });

  const togglePasswordVisibility = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field],
    });
  };

  const dispatch = useDispatch();

  useEffect(() => {
    if (allCountries) setCountry(allCountries[223]);
  }, [allCountries]);

  useEffect(() => {
    setPageTitle("Client Registration");
    dispatch(getModuleStatus({ module: "Sumsub" }));
  }, [dispatch]);

  const validateForm = useCallback(() => {
    let isValid = true;
    setError("");
    companyType.setError("");
    name.setError("");
    mobileNumber.setError("");
    password.setError("");
    confirmPassword.setError("");
    email.setError("");
    acceptTermsAndConditions.setError("");

    if (!companyType.value) {
      companyType.setError("Please select account type");
      setError("Please select account type");
      isValid = false;
    }

    if (!name.value) {
      name.setError("Please enter your name");
      isValid = false;
    }

    if (!introducer.value && company?.show_introducer_field) {
      introducer.setError("Please select referrer");
      isValid = false;
    }

    if (!isSumSubEnabled && !mobileNumber.value) {
      mobileNumber.setError("Mobile is required");
      isValid = false;
    }
    if (email.validate() || !email.value) {
      email.setError("Please enter a valid email");
      isValid = false
    }
    if (password.validate()) isValid = false;
    if (confirmPassword.validate()) isValid = false;
    if (
      password.value &&
      confirmPassword.value &&
      password.value !== confirmPassword.value
    ) {
      confirmPassword.setError("Passwords do not match");
      isValid = false;
    }
    if (!acceptTermsAndConditions.value) {
      acceptTermsAndConditions.setError(
        "Please accept the terms and conditions",
      );
      setError("Please accept the terms and conditions");
      isValid = false;
    }

    return isValid;
    // eslint-disable-next-line
  }, [
    companyType,
    name,
    email,
    password,
    confirmPassword,
    mobileNumber,
    acceptTermsAndConditions,
    isSumSubEnabled,
    introducer,
  ]);

  const handleRegister = async () => {
    setError("");
    if (!validateForm()) return;

    setIsLoading(true);
    setIsModalOpen(true);
    const checkEmail = await dispatch(verifyEmail(email.value));

    if (checkEmail.success) {
      setIsLoading(false);
      setIsModalOpen(false);
      email.setError(checkEmail.message);
      return;
    }

    const data = {
      name: name.value,
      email_address: email.value,
      password: password.value,
      account_type: companyType.value,
      introducer: introducer.value,
    };

    if (!isSumSubEnabled) {
      data.country_code_id = country.id;
      data.mobile_number = mobileNumber.value;
    }

    localStorage.setItem("userDraftData", JSON.stringify(data));
    localStorage.setItem("account_type", companyType.value);

    const res = await dispatch(sendGuestOtp(email.value));
    if (res) {
      setIsModalOpen(true);
    } else {
      setError(res.data.message);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const checkSlider = async () => {
      const enabled = process.env.REACT_APP_SHOW_SLIDER === "true";

      if (!enabled || !company?.login_page_image) {
        setShowSlider(false);
        return;
      }

      const exists = await urlExists(company.login_page_image);

      setShowSlider(exists);
    };

    checkSlider();
  }, [company?.login_page_image]);

  console.log(showSlider);

  const SHOW_SLIDER = showSlider;

  return (
    <main>
      <div className="max-w-[1024px] mx-auto px-4 py-8 min-h-screen flex-1 flex flex-col justify-center">
        <div
          className={`flex flex-col ${
            SHOW_SLIDER
              ? "sm:flex-row justify-center gap-3"
              : "items-center justify-center"
          }`}
        >
          {/* First section */}
          <div
            className={`w-full ${
              SHOW_SLIDER ? "md:w-[572px] sm:flex-grow" : "max-w-[384px]"
            } mb-6 sm:mb-0 space-y-4 flex flex-col items-center`}
          >
            <div className="flex flex-col items-center justify-between w-full max-w-[384px]">
              <Logo className="auth-logo" />

              <div className="w-full flex flex-col items-center justify-center space-y-4 pb-4">
                <p className="auth-page-title !text-4xl font-medium tracking-tight">
                  Create your account
                </p>
                <div className="justify-center items-center flex">
                  <p className="auth-page-sub-title">
                    Already having an account?
                  </p>
                  <SmallButton href="/signin" text="Log in" />
                </div>

                <div className="w-full space-y-4 bg-background-base">
                  {error && (
                    <ErrorMessage
                      message={error}
                      onClose={() => setError("")}
                    />
                  )}

                  <FormInputField
                    id="name"
                    name="name"
                    label="Your name / Business name"
                    type="text"
                    placeholder="Enter your full name or business name"
                    {...name}
                  />

                  <div className="flex items-center justify-between space-x-3">
                    <OptionButton
                      label="Individual Account"
                      value="individual"
                      option={companyType.value}
                      setOption={companyType.setValue}
                      error={companyType.error}
                    />
                    <OptionButton
                      label="Company Account"
                      value="company"
                      option={companyType.value}
                      setOption={companyType.setValue}
                      error={companyType.error}
                    />
                  </div>
                  {!isSumSubEnabled && (
                    <>
                      <div className="flex items-center justify-between space-x-3">
                        {allCountries.length > 0 && country ? (
                          <CountriesInput
                            id="country"
                            countries={allCountries}
                            SelectedCountry={country}
                            onChange={setCountry}
                          >
                            <div className="flex items-center justify-center space-x-3 px-4">
                              {country.flag && (
                                <img
                                  className="rounded-full w-8 h-8"
                                  src={country.flag}
                                  alt="flag"
                                />
                              )}
                              <Label
                                htmlFor="country"
                                className="text-sm font-medium text-text-primary"
                                text={`${
                                  country.phonecode
                                    ? "+" + country.phonecode
                                    : "country"
                                }`}
                              />
                            </div>
                          </CountriesInput>
                        ) : (
                          <Skeleton className="py-6 px-12 rounded-md" />
                        )}

                        <FormInputField
                          id="phone"
                          name="phone"
                          label="Phone"
                          type="number"
                          placeholder="Enter your phone number"
                          {...mobileNumber}
                          error={!!mobileNumber.error}
                        />
                      </div>

                      {mobileNumber.error && (
                        <span className="text-error-500 text-sm">
                          {mobileNumber.error}
                        </span>
                      )}
                    </>
                  )}

                  <FormInputField
                    id="email"
                    name="email"
                    label="Email"
                    type="email"
                    placeholder="Enter your email"
                    {...email}
                  />

                  <FormInputField
                    type={showPassword.new_password ? "text" : "password"}
                    id="password"
                    name="password"
                    label="Password"
                    placeholder="Enter your password"
                    {...password}
                    icon={
                      <PasswordToggleIcon
                        isVisible={showPassword.new_password}
                        onToggle={() =>
                          togglePasswordVisibility("new_password")
                        }
                      />
                    }
                  />

                  <FormInputField
                    type={
                      showPassword.new_password_confirmation
                        ? "text"
                        : "password"
                    }
                    id="confirm_password"
                    name="confirm_password"
                    label="Confirm Password"
                    placeholder="Re-enter your password"
                    {...confirmPassword}
                    icon={
                      <PasswordToggleIcon
                        isVisible={showPassword.new_password_confirmation}
                        onToggle={() =>
                          togglePasswordVisibility("new_password_confirmation")
                        }
                      />
                    }
                  />
                  
                  {company?.show_introducer_field ?
                    <FormInputField
                      id="introducer"
                      name="introducer"
                      label="Referred By"
                      type="text"
                      placeholder="Enter your referred by name"
                      {...introducer}
                    />
                    : ""
                  }

                  <div className="flex items-center space-x-2 pt-4">
                    <input
                      type="checkbox"
                      id="accept_terms"
                      name="accept_terms"
                      checked={acceptTermsAndConditions.value}
                      onChange={(e) =>
                        acceptTermsAndConditions.setValue(e.target.checked)
                      }
                      required
                      className="checkbox-input"
                    />
                    <label
                      htmlFor="accept_terms"
                      className="secondry-lable text-sm cursor-pointer"
                    >
                      By signing up, you agree to our
                      <a
                        href={company && company.terms_and_conditions_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="primary-lable px-2 cursor-pointer"
                      >
                        Terms & Conditions
                      </a>
                    </label>
                  </div>

                  <CommonButton onClick={handleRegister}>Sign up</CommonButton>
                </div>
              </div>
            </div>
            <Footer />
          </div>

          {/* Second section (conditionally rendered) */}
          {SHOW_SLIDER && <Slider />}
        </div>
      </div>
      <RegistrationOTPModal
        isLoading={isLoading}
        setisLoading={setIsLoading}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
      />
    </main>
  );
};

export default Signup;
